import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineEye, HiOutlineEyeOff, HiViewBoards, HiX, HiRefresh, HiChevronUp, HiChevronDown } from 'react-icons/hi';
import { DataListColumn } from './types';

interface ColumnCustomizerProps {
    isOpen: boolean;
    onClose: () => void;
    columns: DataListColumn<any>[];
    visibleColumns: Record<string, boolean>;
    onReorder: (newOrder: string[]) => void;
    onToggleVisibility: (key: string) => void;
    onReset: () => void;
    /** Column keys to hide from the customizer (e.g. cost when not enabled) */
    hiddenColumnKeys?: string[];
    /** Column keys that are locked in place (not draggable, always visible) */
    fixedColumnKeys?: string[];
}

const ColumnCustomizer: React.FC<ColumnCustomizerProps> = ({
    isOpen,
    onClose,
    columns,
    visibleColumns,
    onReorder,
    onToggleVisibility,
    onReset,
    hiddenColumnKeys = [],
    fixedColumnKeys = [],
}) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [isRendered, setIsRendered] = useState(false);

    // Handle mounting for portal
    useEffect(() => {
        setIsRendered(true);
    }, []);

    // Filter out 'action' column and any explicitly hidden columns (e.g. cost)
    const customizedColumns = columns.filter(
        col => col.key !== 'action' && !hiddenColumnKeys.includes(col.key)
    );

    // Handle drag start — prevent dragging fixed columns
    const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
        const col = customizedColumns[index];
        if (fixedColumnKeys.includes(col.key)) {
            e.preventDefault();
            return;
        }
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
        const target = e.target as HTMLElement;
        setTimeout(() => {
            target.style.opacity = '0.5';
        }, 0);
    }, [customizedColumns, fixedColumnKeys]);

    // Handle drag end
    const handleDragEnd = useCallback((e: React.DragEvent) => {
        setDraggedIndex(null);
        setDragOverIndex(null);
        const target = e.target as HTMLElement;
        target.style.opacity = '1';
    }, []);

    // Handle drag over — prevent dropping onto fixed column positions
    const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
        const col = customizedColumns[index];
        if (fixedColumnKeys.includes(col.key)) return; // can't drop onto fixed
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    }, [customizedColumns, fixedColumnKeys]);

    // Handle drop — prevent dropping before fixed columns
    const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        const dragIndex = draggedIndex;
        const dropCol = customizedColumns[dropIndex];

        if (dragIndex === null || dragIndex === dropIndex || fixedColumnKeys.includes(dropCol.key)) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        // Find the first non-fixed index; prevent dropping before it
        const firstMovableIndex = customizedColumns.findIndex(c => !fixedColumnKeys.includes(c.key));
        const effectiveDropIndex = Math.max(dropIndex, firstMovableIndex);

        // Reorder locally
        const newCols = [...customizedColumns];
        const [draggedItem] = newCols.splice(dragIndex, 1);
        newCols.splice(effectiveDropIndex, 0, draggedItem);

        // Update parent with new key order
        const newOrder = newCols.map(c => c.key);
        // Append 'action' if it was filtered out, to keep it at end
        newOrder.push('action'); 
        
        onReorder(newOrder);
        setDraggedIndex(null);
        setDragOverIndex(null);
    }, [draggedIndex, customizedColumns, onReorder, fixedColumnKeys]);

    // Move column up
    const moveUp = useCallback((index: number) => {
        // Find previous movable index
        if (index <= 0) return;
        const prevIndex = index - 1;
        const prevCol = customizedColumns[prevIndex];
        
        // Cannot swap with a fixed column
        if (fixedColumnKeys.includes(prevCol.key)) return;

        const newCols = [...customizedColumns];
        [newCols[prevIndex], newCols[index]] = [newCols[index], newCols[prevIndex]];
        
        const newOrder = newCols.map(c => c.key);
        newOrder.push('action');
        onReorder(newOrder);
    }, [customizedColumns, fixedColumnKeys, onReorder]);

    // Move column down
    const moveDown = useCallback((index: number) => {
        if (index >= customizedColumns.length - 1) return;
        
        const newCols = [...customizedColumns];
        [newCols[index], newCols[index + 1]] = [newCols[index + 1], newCols[index]];
        
        const newOrder = newCols.map(c => c.key);
        newOrder.push('action');
        onReorder(newOrder);
    }, [customizedColumns, onReorder]);

    if (!isOpen || !isRendered) return null;

    const content = (
        <div className="fixed top-0 right-0 bottom-0 z-[100] flex justify-end pointer-events-none" role="dialog">
            {/* Drawer Panel */}
            <div className="pointer-events-auto relative w-80 h-full bg-light-surface dark:bg-[#101928] shadow-2xl flex flex-col border-l border-light-border-default dark:border-[#2c3b55] animate-in slide-in-from-right duration-300">
                {/* Header */}
                <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-light-border-default dark:border-[#2c3b55] bg-light-surfacePrimary dark:bg-[#101928]">
                    <div className="flex items-center gap-2">
                        <div>
                            <h3 className="text-sm font-bold text-light-text-primary dark:text-white">Customize Columns</h3>
                            <p className="text-[10px] text-light-text-secondary dark:text-gray-400">Drag to reorder • Toggle visibility</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-light-interactive-hover dark:hover:bg-slate-600 text-light-text-muted dark:text-gray-400 transition-colors"
                    >
                        <HiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Column List */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {customizedColumns.map((col, index) => {
                        const isVisible = visibleColumns[col.key] !== false;
                        const isFixed = fixedColumnKeys.includes(col.key);
                        
                        return (
                            <div
                                key={col.key}
                                draggable={!isFixed}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDrop={(e) => handleDrop(e, index)}
                                className={`
                                    group flex items-center gap-3 p-2.5 rounded-xl border transition-all duration-200
                                    ${isFixed ? 'cursor-default opacity-80' : 'cursor-grab active:cursor-grabbing'}
                                    ${dragOverIndex === index && !isFixed
                                        ? 'border-primary-light bg-blue-50 dark:bg-blue-900/30 dark:border-blue-500 shadow-md scale-[1.02] z-10'
                                        : isVisible
                                            ? 'border-gray-200 dark:border-slate-700 bg-white dark:bg-[#1a2333] hover:border-primary-light dark:hover:border-blue-500 hover:shadow-md'
                                            : 'border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-[#1a2333]/60 opacity-60 hover:opacity-80'
                                    }
                                `}
                            >
                                {/* Drag Handle — hidden for fixed columns */}
                                <div className={`flex flex-col gap-0.5 transition-colors px-0.5 ${isFixed
                                    ? 'text-transparent'
                                    : 'text-light-text-muted dark:text-gray-600 group-hover:text-primary-light dark:group-hover:text-blue-500 cursor-grab'
                                }`}>
                                    <div className="flex gap-0.5">
                                        <div className="w-1 h-1 rounded-full bg-current" />
                                        <div className="w-1 h-1 rounded-full bg-current" />
                                    </div>
                                    <div className="flex gap-0.5">
                                        <div className="w-1 h-1 rounded-full bg-current" />
                                        <div className="w-1 h-1 rounded-full bg-current" />
                                    </div>
                                    <div className="flex gap-0.5">
                                        <div className="w-1 h-1 rounded-full bg-current" />
                                        <div className="w-1 h-1 rounded-full bg-current" />
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <span className={`text-xs font-semibold ${isVisible ? 'text-gray-800 dark:text-gray-200' : 'text-gray-500 dark:text-gray-500'}`}>
                                        {col.label}
                                    </span>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-1">
                                    {/* Move up/down buttons */}
                                    {!isFixed && (
                                        <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity mr-1">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); moveUp(index); }}
                                                className={`p-0.5 rounded text-gray-400 hover:text-primary-light hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${index === 0 || fixedColumnKeys.includes(customizedColumns[index-1]?.key) ? 'opacity-30 cursor-not-allowed' : ''}`}
                                                title="Move up"
                                                disabled={index === 0 || fixedColumnKeys.includes(customizedColumns[index-1]?.key)}
                                            >
                                                <HiChevronUp className="w-3 h-3" />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); moveDown(index); }}
                                                className={`p-0.5 rounded text-gray-400 hover:text-primary-light hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${index === customizedColumns.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                                                title="Move down"
                                                disabled={index === customizedColumns.length - 1}
                                            >
                                                <HiChevronDown className="w-3 h-3" />
                                            </button>
                                        </div>
                                    )}

                                    {/* Visibility toggle */}
                                    {!isFixed ? (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onToggleVisibility(col.key); }}
                                            className={`p-1.5 rounded-lg transition-all duration-200 ${isVisible
                                                ? 'bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60'
                                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-500 dark:hover:bg-slate-600'
                                                }`}
                                            title={isVisible ? 'Hide column' : 'Show column'}
                                        >
                                            {isVisible ? (
                                                <HiOutlineEye className="w-4 h-4" />
                                            ) : (
                                                <HiOutlineEyeOff className="w-4 h-4" />
                                            )}
                                        </button>
                                    ) : (
                                        <div className="px-2 py-1 rounded text-[10px] bg-gray-100 text-gray-500 font-medium uppercase tracking-wide border border-transparent dark:bg-slate-700 dark:text-gray-400">
                                            Pinned
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {/* Footer */}
                <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-t border-light-border-default dark:border-[#2c3b55] bg-light-surfaceSecondary/30 dark:bg-[#101928]">
                    <button
                        onClick={onReset}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-light-text-secondary dark:text-gray-400 hover:text-primary-light dark:hover:text-blue-400 transition-colors"
                    >
                        <HiRefresh className="w-3.5 h-3.5" />
                        Reset to Default
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 text-xs font-semibold text-white bg-[#1d64f3] hover:bg-[#1856da] rounded-lg shadow-md hover:shadow-lg transition-all"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(content, document.body);
};

export default ColumnCustomizer;
